import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Button } from '@mui/material';

import { loadCourseTemplate, updateCourseModuleParticipants } from '../../../store/courseService';
import { selectUsersForNames } from '../../../store/userSelectors';
import { loadUsersMinimalDetails } from '../../../store/userService';
import { isTimePast } from '../../../utils/dateFunctions';
import LabelledTextField from '../../ui/editors/LabelledTextField';
import LoadingSpinner from '../../ui/LoadingSpinner';
import ParticipantAttendanceRegisterView from '../modules/participantAttendance/ParticipantAttendanceRegisterView';
import { SequentialNotice } from '../utils/SequentialNotice';

import cardClasses from '../../../commonStyles/cardContainer.module.css';
import dropdownClasses from '../courseStyles/rowDropDown.module.css';

const CHARACTER_LIMIT = 1000;

const AllActiveCourseDetails = ({ row, onClose, isDisabled = false }) => {
    const { handleSubmit } = useForm({});

    // HOOKS
    const dispatch = useDispatch();

    // HANDLERS
    const handleInformationChange = (e) => {
        setInformation(e.target.value);
    };

    // LOCAL STATE
    const msg = `Module details for course ${row.name} have been updated`;
    const [information, setInformation] = useState('');
    const [canBeSubmittedToCourse, setCanBeSubmittedToCourse] = useState(true);
    const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);

    // STORE STATE
    const users = useSelector(selectUsersForNames);
    const attendanceDetails = useSelector(
        (state) => state.entities.directusService.attendanceDetails
    );
    const currentTemplate = useSelector((state) => state.entities.courseService.currentTemplate);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        if (!row || Object.keys(row)?.length === 0) return;
        dispatch(loadCourseTemplate(row.courseTemplateId));
        const userIds = [...new Set(row.modules.map((el) => el.userId))];
        const missingAdviserIds = userIds.filter((el) => !users.find((entry) => entry.id === el));
        missingAdviserIds.length > 0 && dispatch(loadUsersMinimalDetails(missingAdviserIds));
        const checkTimes = row.modules.every((el) => isTimePast(`${el.date}T${el.endTime}`));
        if (checkTimes) setCanBeSubmittedToCourse(!checkTimes);
    }, [row]);

    useEffect(() => {
        if (successMessage === msg) {
            setIsSubmitButtonDisabled(false);
            setCanBeSubmittedToCourse(false);
        }
    }, [successMessage]);

    //  FORM SUBMIT
    const onSubmit = () => {
        setIsSubmitButtonDisabled(true);
        const selectedCourseStatus = attendanceDetails.find(
            (item) => item.name.toLowerCase() === 'not known'
        );

        const updatedModules = row.modules.map((el) => ({
            id: el.id,
            participants: [
                ...el.participants,
                {
                    participantId: currentParticipant.id,
                    statusId: selectedCourseStatus.id,
                    notes: information
                }
            ]
        }));

        updatedModules.forEach((el) => {
            const { id, participants } = el;
            const payload = { participants };
            dispatch(updateCourseModuleParticipants(id, payload, msg));
        });
    };

    return !currentTemplate ||
        currentTemplate.length < 1 ||
        attendanceDetails?.length < 1 ||
        canBeSubmittedToCourse === '' ? (
        <LoadingSpinner content="No course/module information found" />
    ) : (
        <div
            onSubmit={(e) => {
                //  This stops the Submit event from bubbling up to the form enclosing this component
                e.preventDefault();
                e.stopPropagation();
            }}>
            <form onSubmit={handleSubmit(onSubmit)} data-testid="active_course_details_form_start">
                <div className={dropdownClasses.rowDropdown}>
                    <h3>{row.name} ({row.code})</h3>
                    <p className={dropdownClasses.description}>{row.description}</p>
                    <div className={cardClasses.cardContainer}>
                        <h3>Module selection</h3>
                        <div hidden={!currentTemplate.modulesInSetOrder}>
                            <SequentialNotice />
                        </div>
                        <div className={cardClasses.cards}>
                            {row.modules.map((el) => (
                                <div key={el.id}>
                                    <ParticipantAttendanceRegisterView
                                        module={el}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    <LabelledTextField
                        id={'information'}
                        mandatory={false}
                        rows={2}
                        placeholder={
                            'Please type Additional information about your participant for the trainer here'
                        }
                        counter={'true'}
                        helperText={`${information.length}` + '/' + CHARACTER_LIMIT}
                        inputProps={{
                            maxLength: CHARACTER_LIMIT
                        }}
                        disabled={isDisabled}
                        onChange={handleInformationChange}
                    />
                    <Button
                        sx={{ marginTop: '-30px', marginBottom: '20px' }}
                        disabled={
                            !canBeSubmittedToCourse ||
                            isSubmitButtonDisabled ||
                            isDisabled
                        }
                        type="submit"
                        color="primary"
                        variant="contained"
                        data-testid="testIdSubmitButton1"
                        endIcon={<KeyboardArrowRightIcon />}>
                        {'Submit Participant To This Course'}
                    </Button>
                    <div className={dropdownClasses.exit} onClick={onClose}>
                        Close details
                        <CloseIcon />
                    </div>
                </div>
            </form>
        </div>
    );
};

AllActiveCourseDetails.propTypes = {
    row: PropTypes.object.isRequired,
    isDisabled: PropTypes.bool,
    onClose: PropTypes.func.isRequired
};

export default AllActiveCourseDetails;
